import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { TonConnectButton, useTonWallet } from "@tonconnect/ui-react";
import { connectWS, rpcCall, subscribe } from "../ws/client";
import { ProtocolDescription, JsonRpcNotification } from "../ws/types";
import { ToastProvider, toast } from "../components/ui/Toast";
import OrderList from "../components/OrderList";
import MyOrders from "../components/MyOrders";
import MyDeals from "../components/MyDeals";
import WalletBalance from "../components/WalletBalance";
import EscrowBalance from "../components/EscrowBalance";
import { PaymentDetails } from "../components/PaymentDetails";
import Requests from "../components/Requests";
import ReferralProgram from "../components/ReferralProgram";
import Lottery from "../components/Lottery";
import CoinFlip from "../components/CoinFlip";
import TradingPause from "../components/TradingPause";
import PendingTransactions from "../components/PendingTransactions";
import { useWalletDebug } from "../hooks/useWalletDebug";
import { useTelegram } from "../providers/TelegramProvider";
import "../styles/main.scss";
import "../styles/Header.scss";

interface ExchangeRate {
    ton_rub: number | null;
    ton_usd: number | null;
    usd_rub: number | null;
}

function HeaderRates() {
    const [exchangeRate, setExchangeRate] = useState<ExchangeRate>({ ton_rub: null, ton_usd: null, usd_rub: null });
    const [trendClass, setTrendClass] = useState<"" | "flash-up" | "flash-down">("");
    const [trendDirection, setTrendDirection] = useState<"up" | "down" | "flat">("flat");
    const previousTonRubRef = useRef<number | null>(null);
    const flashTimeoutRef = useRef<number | null>(null);

    const formatRub = (value: number | null | undefined) => {
        if (value === null || value === undefined || Number.isNaN(value)) {
            return "--";
        }
        return `${value.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽`;
    }; 

    const loadRates = async () => {
        try {
            const baseResponse = await rpcCall<{}, ExchangeRate>("exchange_rate.get", {});
            if (baseResponse.result) {
                const nextTonRub = baseResponse.result.ton_rub;
                const prevTonRub = previousTonRubRef.current;

                if (typeof nextTonRub === "number" && typeof prevTonRub === "number" && nextTonRub !== prevTonRub) {
                    const nextTrendClass = nextTonRub > prevTonRub ? "flash-up" : "flash-down";
                    setTrendDirection(nextTonRub > prevTonRub ? "up" : "down");

                    if (flashTimeoutRef.current) {
                        clearTimeout(flashTimeoutRef.current);
                    }

                    // Re-apply class to retrigger animation even for repeated same direction.
                    setTrendClass("");
                    window.setTimeout(() => {
                        setTrendClass(nextTrendClass);
                    }, 10);

                    flashTimeoutRef.current = window.setTimeout(() => {
                        setTrendClass("");
                    }, 900);
                }
                if (typeof nextTonRub === "number" && typeof prevTonRub === "number" && nextTonRub === prevTonRub) {
                    setTrendDirection("flat");
                }

                previousTonRubRef.current = typeof nextTonRub === "number" ? nextTonRub : null;
                setExchangeRate(baseResponse.result);
            }
        } catch (error) {
            console.error("Failed to load header rates:", error);
        }
    };

    useEffect(() => {
        loadRates();

        const unsubscribe = subscribe("rates.updated", () => {
            loadRates();
        });

        // Fallback polling in case websocket rate event is missed.
        const intervalId = window.setInterval(() => {
            loadRates();
        }, 30000);

        return () => {
            unsubscribe();
            clearInterval(intervalId);
            if (flashTimeoutRef.current) {
                clearTimeout(flashTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="header-rates" aria-label="Текущие курсы">
            <div className={`rate-pill ${trendClass}`}>
                <span className={`rate-trend ${trendDirection}`} aria-hidden="true">
                    {trendDirection === "up" ? "▲" : trendDirection === "down" ? "▼" : "•"}
                </span>
                <span className="rate-pair">TON/RUB</span>
                <strong>{formatRub(exchangeRate.ton_rub)}</strong>
            </div>
        </div>
    );
}

type HomeTab =
    | "orders"
    | "my-orders"
    | "my-deals"
    | "payment-details"
    | "requests"
    | "referral"
    | "liquidity"
    | "lottery"
    | "games";

interface HeaderProps {
    activeTab: HomeTab;
    setActiveTab: (tab: HomeTab) => void;
    menuOpen: boolean;
    setMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
    tgUser: ReturnType<typeof useTelegram>["user"];
}

function Header({ activeTab, setActiveTab, menuOpen, setMenuOpen, tgUser }: HeaderProps) {
    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [menuOpen]);

    const menuItems = [
        { id: "orders", label: "Ордера", icon: "📋" },
        { id: "requests", label: "Заявки", icon: "📬" },
        { id: "my-deals", label: "Сделки", icon: "🤝" },
        { id: "payment-details", label: "Реквизиты", icon: "💳" },
        { id: "referral", label: "Рефералы", icon: "🎁" },
        { id: "lottery", label: "Лотерея", icon: "🎟" },
        { id: "games", label: "Игры", icon: "🎰" },
        { id: "liquidity", label: "Пул", icon: "💎" },
    ] as const;

    const popup = menuOpen && createPortal(
        <div className="menu-popup" onClick={() => setMenuOpen(false)}>
            <div className="menu-popup-content" onClick={(event) => event.stopPropagation()}>
                <div className="menu-popup-header">
                    <div>
                        <span className="menu-eyebrow">Навигация</span>
                    </div>
                    <button
                        className="menu-close"
                        onClick={() => setMenuOpen(false)}
                        aria-label="Закрыть меню"
                    >
                        ✕
                    </button>
                </div>
                <div className="menu-items-grid">
                    {menuItems.map((item) => (
                        <button
                            key={item.id}
                            className={activeTab === item.id ? "menu-item active" : "menu-item"}
                            onClick={() => {
                                setActiveTab(item.id);
                                setMenuOpen(false);
                            }}
                        >
                            <span className="menu-item-icon">{item.icon}</span>
                            <span className="menu-item-label">{item.label}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    );

    return (
        <>
            <header className="header">
                <div className="header-content">
                    <div className="header-left">
                        <div className="brand-copy">
                            <h1>JET P2P</h1>
                        </div>
                        <HeaderRates />
                    </div>
                    <div className="header-right">
                        {tgUser && (
                            <span className="telegram-user">
                                <img
                                    width="24"
                                    height="24"
                                    src={tgUser.photo_url}
                                    alt="👤"
                                />
                                <span className="telegram-user-name">
                                    {tgUser.first_name} {tgUser.last_name || ""}
                                </span>
                            </span>
                        )}
                        <div className="wallet-connect-slot">
                            <TonConnectButton style={{ width: "100%" }} />
                        </div>
                        <button
                            className={"menu-toggle modern-toggle" + (menuOpen ? " open" : "")}
                            onClick={() => setMenuOpen(!menuOpen)}
                            aria-label={menuOpen ? "Закрыть меню" : "Открыть меню"}
                        >
                            <span className="hamburger">
                                <span className="bar top" />
                                <span className="bar middle" />
                                <span className="bar bottom" />
                            </span>
                        </button>
                    </div>
                </div>
            </header>
            {popup}
        </>
    );
}

export default function Home() {
    const wallet = useTonWallet();
    const { user: tgUser } = useTelegram();
    const [searchParams] = useSearchParams();
    useWalletDebug();

    const [status, setStatus] = useState<"connecting" | "connected" | "disconnected">("connecting");
    const [protocol, setProtocol] = useState<ProtocolDescription | null>(null);
    const [activeTab, setActiveTab] = useState<HomeTab>("orders");
    const [menuOpen, setMenuOpen] = useState(false);
    const [reconnectAttempt, setReconnectAttempt] = useState(0);
    const { startParam: tgStartParam } = useTelegram();

    useEffect(() => {
        let ws: WebSocket;
        let reconnectTimeout: number;
        let closedByCleanup = false;

        const connect = () => {
            ws = connectWS((data: JsonRpcNotification) => {
                if (data.method === "protocol.describe") {
                    setProtocol(data.params as ProtocolDescription);
                }
            });

            ws.addEventListener("open", () => {
                setStatus("connected");
                setReconnectAttempt(0);
            });

            ws.addEventListener("close", () => {
                if (closedByCleanup) {
                    return;
                }

                setStatus("disconnected");

                const delay = Math.min(1000 * Math.pow(2, reconnectAttempt), 30000);
                setReconnectAttempt((prev) => prev + 1);

                reconnectTimeout = window.setTimeout(() => {
                    setStatus("connecting");
                    connect();
                }, delay);
            });
        };

        connect();

        return () => {
            closedByCleanup = true;
            if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
            }
            if (ws) {
                ws.close();
            }
        };
    }, [reconnectAttempt]);

    useEffect(() => {
        if (tgStartParam?.startsWith("ref_")) {
            const code = tgStartParam.replace("ref_", "").toUpperCase();
            localStorage.setItem("jetp2p_referral_code", code);
        }
    }, [tgStartParam]);

    useEffect(() => {
        const registerReferral = async () => {
            if (!wallet?.account?.address) return;
            if (status !== "connected") return;
            const alreadyRegistered = localStorage.getItem("jetp2p_referral_registered");
            if (alreadyRegistered) return;

            const refCode = localStorage.getItem("jetp2p_referral_code") || searchParams.get("ref");
            if (!refCode) return;

            try {
                const response = await rpcCall<
                    { referrer_code: string; referred_wallet: string },
                    { referral: unknown }
                >("referral.register", {
                    referrer_code: refCode,
                    referred_wallet: wallet.account.address,
                });

                if (response.error) {
                    if (response.error.message?.includes("already referred")) {
                        localStorage.setItem("jetp2p_referral_registered", "true");
                    }
                } else if (response.result?.referral) {
                    localStorage.setItem("jetp2p_referral_registered", "true");
                    toast.success("Вы пришли по реферальной ссылке! Ваш друг получит 10% от ваших сделок.");
                }
            } catch (error) {
                console.error("Failed to register referral:", error);
            }
        };

        registerReferral();
    }, [wallet, searchParams, tgStartParam, status]);

    useEffect(() => {
        const tabParam = searchParams.get("tab");
        if (tabParam && ["my-orders", "my-deals", "payment-details", "requests", "referral", "liquidity", "games", "lottery"].includes(tabParam)) {
            setActiveTab(tabParam as HomeTab);
        }
    }, [searchParams]);

    useEffect(() => {
        if (!wallet?.account?.address || !tgUser?.id) return;

        const linkTelegram = async () => {
            try {
                await rpcCall<
                    { wallet: string; chat_id: string; user_data?: unknown },
                    unknown
                >("telegram.link", {
                    wallet: wallet.account.address,
                    chat_id: String(tgUser.id),
                    user_data: {
                        id: tgUser.id,
                        first_name: tgUser.first_name,
                        last_name: tgUser.last_name,
                        username: tgUser.username,
                        language_code: tgUser.language_code,
                        is_premium: tgUser.is_premium,
                        photo_url: tgUser.photo_url,
                    },
                });
            } catch (err) {
                console.error("Failed to link Telegram:", err);
            }
        };

        linkTelegram();
    }, [wallet, tgUser]);

    useEffect(() => {
        const dealId = searchParams.get("deal");
        const tabParam = searchParams.get("tab");
        if (tabParam) {
            setActiveTab(tabParam as HomeTab);
        }
        if (dealId) {
            sessionStorage.setItem("open_deal_id", dealId);
        }
    }, [searchParams]);

    const reloadOrders = () => {
        window.dispatchEvent(new CustomEvent("orders:refresh"));
    };

    return (
        <ToastProvider>
            <div className="layout">
                <Header
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    menuOpen={menuOpen}
                    setMenuOpen={setMenuOpen}
                    tgUser={tgUser}
                />
                <main className="main">
                    {!protocol && (
                        <div className="loading-container">
                            <div className="spinner-lg" />
                            <span className="loading-text">
                                {status === "disconnected" ? "Переподключение..." : "Загрузка протокола..."}
                            </span>
                        </div>
                    )}

                    {protocol && (
                        <>
                            <PendingTransactions />

                            <div className="tab-content">
                                {activeTab === "orders" && <OrderList />}
                                {activeTab === "my-orders" && <MyOrders />}
                                {activeTab === "requests" && <Requests />}
                                {activeTab === "my-deals" && <MyDeals />}
                                {activeTab === "payment-details" && wallet && (
                                    <PaymentDetails wallet={wallet.account.address} />
                                )}
                                {activeTab === "referral" && <ReferralProgram />}
                                {activeTab === "liquidity" && <EscrowBalance />}
                                {activeTab === "lottery" && <Lottery />}
                                {activeTab === "games" && <CoinFlip />}
                            </div>
                        </>
                    )}
                </main>
                <footer className="footer">
                    <TradingPause onTradingPauseChange={reloadOrders} />
                    <WalletBalance />
                </footer>
            </div>
        </ToastProvider>
    );
}
